import { useEffect, useState } from "react";
import DateInput from '../../components/DateInput';
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { BASE_URL } from "../../config";
import { showSuccess } from "../../utils/dialog";
import { SignaturePanel } from "../../shared/SignaturePanel";
import "../../Styles/AccessibilityStndCustmer.css";
import talentLogo from "../../assets/talentlogo2.png";

const EmploymentStandards = () => {
  const navigate = useNavigate();
  const itemId = "employment-standards";
  const fieldOrder = ["date", "employeeName", "employeeSignature"];
  const [formData, setFormData] = useState(() => {
    const accountId = localStorage.getItem("accountId");
    const saved = localStorage.getItem(`form_${accountId}_${itemId}`);
    const initial = saved ? JSON.parse(saved) : {};
    return fieldOrder.reduce((acc, f) => {
      acc[f] = (initial[f] ?? "");
      return acc;
    }, {});
  });

  const [fieldErrors, setFieldErrors] = useState({});
  const [err, setErr] = useState("");

  useEffect(() => {
    const accountId = localStorage.getItem("accountId");
    const saved = localStorage.getItem(`form_${accountId}_${itemId}`);
    if (saved) {
      const raw = JSON.parse(saved);
      const normalized = fieldOrder.reduce((acc, f) => {
        acc[f] = (raw[f] ?? "");
        return acc;
      }, {});
      setFormData(normalized);
    }
  }, []);

  // Auto-save draft. Never set "complete" here (only on Submit). If already complete, preserve it when user reopens and goes back.
  useEffect(() => {
    const accountId = localStorage.getItem("accountId");
    const existing = (accountId ? localStorage.getItem(`status_${accountId}_${itemId}`) : null) || localStorage.getItem(`status_${itemId}`);
    const values = fieldOrder.map((f) => (formData[f] ?? "").toString().trim());
    const allEmpty = values.every((v) => v.length === 0);
    const statusToPersist = allEmpty ? "incomplete" : (existing === "complete" ? "complete" : "in-progress");
    localStorage.setItem(`status_${accountId}_${itemId}`, statusToPersist);
    localStorage.setItem(`form_${accountId}_${itemId}`, JSON.stringify(formData));
    if (accountId && statusToPersist !== "incomplete") {
      axios.post(`${BASE_URL}/api/registration/${accountId}/status/${itemId}`, {
        status: statusToPersist,
        formData: {}
      }).catch(err => console.error("Failed to sync status:", err));
    }
  }, [formData]);

  // const isDateToday = (val) => {
  //   if (!val) return false;
  //   const input = new Date(val);
  //   const now = new Date();
  //   return (
  //     input.getFullYear() === now.getFullYear() &&
  //     input.getMonth() === now.getMonth() &&
  //     input.getDate() === now.getDate()
  //   );
  // };

  const isDateToday = (val) => {
    if (!val) return false;
    
    // Get current date in User's Local Time
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0'); // Months are 0-indexed
    const day = String(now.getDate()).padStart(2, '0');

    // Create a standardized string "YYYY-MM-DD"
    const todayString = `${year}-${month}-${day}`;

    // Compare the strings directly
    return val === todayString;
  };

  const handleSubmit = (e) => {
    if (e?.preventDefault) e.preventDefault();
    setErr("");
    setFieldErrors({});

    const errors = {};
    if (!formData.date) errors.date = "This field is required";
    else if (!isDateToday(formData.date)) errors.date = "Date must be today";
    if (!String(formData.employeeName || "").trim()) errors.employeeName = "This field is required";
    if (!formData.employeeSignature) errors.employeeSignature = "Signature is required";

    if (Object.keys(errors).length) {
      setFieldErrors(errors);
      setErr("Please fill the required fields!");
      return;
    }

    const accountId = localStorage.getItem("accountId");
    localStorage.setItem(`form_${accountId}_${itemId}`, JSON.stringify(formData));
    const values = fieldOrder.map((f) => (formData[f] ?? "").toString().trim());
    const allEmpty = values.every((v) => v.length === 0);
    const allFilled = values.every((v) => v.length !== 0);
    const newStatus = allEmpty ? "incomplete" : allFilled ? "complete" : "in-progress";
    const payload = { status: newStatus, formData };
    if (accountId && newStatus === 'complete') {
      axios.post(`${BASE_URL}/api/registration/${accountId}/items/${itemId}/complete`, payload)
        .catch(err => console.error('Failed to save employment standards item:', err))
        .finally(() => {
          axios.post(`${BASE_URL}/api/registration/${accountId}/status/${itemId}`, { status: newStatus, formData: {} }).catch(() => {});
          showSuccess("Form completed successfully!");
          navigate('/register');
        });
    } else {
      navigate('/register');
    }
  };

  const handleBack = () => {
    navigate("/register");
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setFieldErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      if (String(value || "").trim()) delete next[field];
      return next;
    });
  };

  return (
    <div className="accessibility-form-page">
      <div className="form-container">
        {/* Top content card */}
        <div className="form-card">
          <header className="form-card-header" style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
            <img src={talentLogo} alt="Talent Employment Inc." style={{ height: 64, marginBottom: 10 }} />
            <h2 className="form-title" style={{ textAlign: "center" }}>
              Employment Standards Rights for Temporary Help Agency Assignment Employees
            </h2>
          </header>

          <section className="form-card-content">
            {err && (<div className="error-text" style={{ color: "#dc2626", marginBottom: 12 }}>{err}</div> )}
            <p className="form-intro">
              <strong>Disclaimer:</strong> This resource has been prepared to help employees and employers understand some of the minimum rights and obligations established under the Employment Standards Act, 2000 (ESA) and regulations. It is not legal advice. It is not intended to replace the ESA or regulations and reference should always be made to the official version of the legislation. Although we endeavor to ensure that the information in this resource is as current and accurate as possible, errors do occasionally occur. The ESA provides minimum standards only. Some employees may have greater rights under an employment contract, collective agreement, the common law or other legislation. 
            </p>
            <p className="form-intro">
                <a href="https://www.ontario.ca/laws/statute/00e41?_ga=2.173408935.584403115.1583168406-1699692134.1480627715" class="red-link" target="_blank" rel="noopener noreferrer">
                    The Employment Standards Act, 2000
                </a> (ESA) sets minimum standards for most workplaces in Ontario, such as minimum wage and limits on hours of work. Special rules and exemptions apply to certain employees, including assignment employees of temporary help agencies. This information sheet gives information about your rights as an assignment employee. For more information on your rights visit 
                <a href="https://www.ontario.ca/ESAquide" class="red-link" target="_blank" rel="noopener noreferrer">
                    {" "} Ontario.ca/ESAguide
                </a> and select Temporary Help Agencies.
            </p>
            <p className="form-intro">
                In addition to this information sheet, the temporary help agency must also provide you with the Employment Standards Poster within 30 days of starting your job. If your first language is not English, your employer must provide the poster to you in your first language, if you ask for it and it is available from the Ministry of Labour. Training and Skills Development. The poster is available for download at 
                <a href="https://www.ontario.ca/page/posters-required-workplace#section-1" class="red-link" target="_blank" rel="noopener noreferrer">
                    {" "}  Ontario.ca/ESAposter
                </a>.
            </p>
            <p className="form-intro topic1">
              Are you an assignment employee? 
            </p>
            <p className="form-intro">
              You are an assignment employee of a temporary help agency if you and the temporary help agency agree that it will help you find temporary work assignments. 
            </p>
            <p className="form-intro"  style={{ fontSize: "14px" }}>
              <strong>Your temporary help agency is your employer even when you are not assigned to work for one of their client businesses.</strong>
            </p>
            <p className="form-intro topic1">
              Your temporary help agency must provide you information  
            </p>
            <p className="form-intro paragraph-space">
              Your temporary help agency must tell you their legal name, any other names used by the temporary help agency and contact information in writing as soon as possible after you are hired. 
            </p>
            <p className="form-intro paragraph-space">
              When your temporary help agency offers you an assignment with a client business, the agency must give you the following information in writing as soon as possible: 
            </p>
            <ul className="form-bullets">
              <li>• the client business' legal name, any other names used by the client business and contact information </li>
              <li>• your wage rate and benefits </li>
              <li>• the hours you will be working </li>
              <li>• a description of the work you will be doing </li>
              <li>• the length (term) of the assignment (if known) </li>
              <li>• your pay period and pay day </li>
            </ul>
            <p className="form-intro topic1">
              Client businesses can give you job references or hire you 
            </p>
            <p className="form-intro paragraph-space">
              Your temporary help agency cannot: 
            </p>
            <ul className="form-bullets">
              <li>• stop a client business from giving you a job reference </li>
              <li>• stop a client business from hiring you directly </li>
              <li>• charge you a fee if a client business wants to hire you </li>
            </ul>
            <p className="form-intro topic1">
              Your temporary help agency and client business must both keep records
            </p>
            <p className="form-intro paragraph-space">
              Your temporary help agency must record the number of hours you work for each client business in each day and each week. A client business must also record the number hours you work for them in each day and each week  
            </p>
            <p className="form-intro topic1">
              Vacation time and pay 
            </p>
            <p className="form-intro paragraph-space">
              You have the right to vacation time and vacation pay. The amount of vacation time and vacation pay depends on how long you have worked for your temporary help agency. If you have worked for your temporary help agency for less than five years, you have the right to two weeks of vacation after every twelve-month vacation entitlement year and vacation pay of four percent of your total wages. If you have worked for your temporary help agency for five or more years, you have the right to three weeks of vacation time after every twelve-month vacation entitlement year and vacation pay of six percent of your total wages. 
            </p>
            <p className="form-intro paragraph-space">
              You have the right to vacation time and vacation pay. The amount of vacation time and vacation pay depends on how long you have worked for your temporary help agency. If you have worked for your temporary help agency for less than five years, you have the right to two weeks of vacation after every twelve-month vacation entitlement year and vacation pay of four percent of your total wages. If you have worked for your temporary help agency for five or more years, you have the right to three weeks of vacation time after every twelve-month vacation entitlement year and vacation pay of six percent of your total wages. 
            </p>
            <p className="form-intro paragraph-space">
              For example: Syed has worked for his temporary help agency for three years. He can take two weeks of vacation and earns four percent in vacation pay. He earns $1,500.00 every two weeks. Syed and his temporary help agency agreed that he will be paid vacation pay on each pay cheque. Syed is paid $60.00 ($1,500 x 4%) in vacation pay on each pay cheque.
            </p>
            <p className="form-intro paragraph-space">
              Learn more about vacation time and vacation pay at   
              <a href="https://www.ontario.ca/document/your-guide-employment-standards-act-0/vacation" class="red-link" target="_blank" rel="noopener noreferrer">
                    {" "}  Ontario.ca/vacation
                </a>.
            </p>
            <p className="form-intro topic1">
              Public holidays
            </p>
            <p className="form-intro paragraph-space">
              Ontario has nine public holidays each year:
            </p>
            <ul className="form-bullets">
              <li>• New Year’s Day</li>
              <li>• Family Day</li>
              <li>• Good Friday</li>
              <li>• Victoria Day</li>
              <li>• Canada Day</li>
              <li>• Labour Day</li>
              <li>• Thanksgiving Day</li>
              <li>• Christmas Day</li>
              <li>• Boxing Day</li>
            </ul>
            <p className="form-intro paragraph-space">
              If you are on assignment and there is a public holiday on one of your working days, generally you have the right to take the public holiday off work and be paid public holiday pay.
            </p>
            
          </section>
          <div className="grid-2">
            <div className="form-field">
              <label htmlFor="date">Date*</label>
              <DateInput
                id="date"
                value={formData.date}
                onChange={(v) => handleInputChange("date", v)}
                required
              />
              {fieldErrors.date && <div className="error-text" style={{ color: "#dc2626", marginTop: 6 }}>{fieldErrors.date}</div>}
            </div>

            <div className="form-field">
              <label htmlFor="employeeName">Employee's Full Name*</label>
              <input
                id="employeeName"
                type="text"
                value={formData.employeeName}
                onChange={(e) =>
                  handleInputChange("employeeName", e.target.value)
                }
                required
              />
              {fieldErrors.employeeName && <div className="error-text" style={{ color: "#dc2626", marginTop: 6 }}>{fieldErrors.employeeName}</div>}
            </div>
          </div>

          {/* Signature panel in a boxed wrapper */}
          <div className="signature-panel-wrapper">
            {/* <label>Employee's Signature*</label> */}
            <SignaturePanel
              onSignatureChange={(dataURL) =>
                handleInputChange("employeeSignature", dataURL)
              }
              initialSignature={formData.employeeSignature}
              required
            />
            {fieldErrors.employeeSignature && <div className="error-text" style={{ color: "#dc2626", marginTop: 6 }}>{fieldErrors.employeeSignature}</div>}
          </div>

          <div className="form-buttons">
            <button type="button" onClick={handleBack}>Back</button>
            <button type="submit" onClick={handleSubmit}>Submit</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmploymentStandards;
